/**
 * Autopilot (ADR 0073, ORGU-82): per project, Orgus starts the next ready backlog item as a
 * mission whenever there is room. Settings, the pure pick (what it takes next and why it skips
 * the rest) and the status the Missions page shows. The hub module 'missions' runs it on its tick.
 */
import type { Id, Ts } from '../index.ts';
import { ITEM_KIND_LABEL, MISSION_KINDS } from './missions.ts';
import type { BacklogItem, ItemKind, ItemLink } from './missions.ts';
import { openBlockers, tooLarge } from './requirements.ts';
import { ITEM_REVIEW_MODES } from './review.ts';
import type { ItemReviewMode } from './review.ts';
import { SESSION_PERMISSION_MODES } from './scheduler.ts';
import { agentKindLabel } from './adapters.ts';
import { FAILURE_CAUSE_LABEL } from './failures.ts';
import type { FailureCause } from './failures.ts';

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/** One project's Autopilot. PUT /api/missions/autopilot/:projectId */
export interface AutopilotSettings {
  on: boolean;
  /** Missions it runs at once in this project. */
  maxMissions: number;
  /** USD its missions may spend per day (local day, the costs module's ledger); null: no cap. */
  dailyCapUsd: number | null;
  /** Item kinds it takes. Never epics or features: they run as their stories. */
  kinds: ItemKind[];
  /** The permission mode its sessions run in; null: the machine default. */
  permissionMode: string | null;
  /**
   * Who reviews finished items (ORGU-85, review.ts): orgus, people, or null for the default
   * (Orgus when the project's Autonomy is full, else people). Absent in older files: null.
   */
  review?: ItemReviewMode | null;
  /** Who changed it last, and when: its starts are made on that person's behalf (ADR 0042). */
  by: string;
  at: Ts;
}

export const AUTOPILOT_MAX_MISSIONS = 10;
export const AUTOPILOT_KINDS: readonly ItemKind[] = MISSION_KINDS;
/** Failed missions after which an item goes to triage and Autopilot leaves it to a person. */
export const AUTOPILOT_FAILURES = 2;
/**
 * Missions that could not run through no fault of the item (its agent signed out or out of quota,
 * a start the machine refused, a session that never reported or was interrupted) after which an
 * item goes to a person too (ADR 0078). They are not failures of the item, so they get more tries.
 */
export const AUTOPILOT_SETBACKS = 4;
/** The label that parks an item: Autopilot leaves it alone while it has it. */
export const PARKED_LABEL = 'parked';

export const AUTOPILOT_DEFAULTS: Readonly<Omit<AutopilotSettings, 'by' | 'at'>> = Object.freeze({
  on: false,
  maxMissions: 2,
  dailyCapUsd: null,
  kinds: ['story', 'defect', 'task'] as ItemKind[],
  permissionMode: null,
  review: null,
});

export type AutopilotPatch = Partial<Pick<AutopilotSettings, 'on' | 'maxMissions' | 'dailyCapUsd' | 'kinds' | 'permissionMode' | 'review'>>;

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Why a settings change is not valid, or null. */
export function autopilotPatchError(v: unknown): string | null {
  if (!isObj(v)) return 'Send the settings as a JSON object.';
  const known = ['on', 'maxMissions', 'dailyCapUsd', 'kinds', 'permissionMode', 'review'];
  const keys = Object.keys(v);
  if (!keys.length) return 'Nothing to change.';
  const odd = keys.find((k) => !known.includes(k));
  if (odd) return `${odd} is not an Autopilot setting. They are ${known.join(', ')}.`;
  if (v.on !== undefined && typeof v.on !== 'boolean') return 'Say on: true or on: false.';
  if (v.maxMissions !== undefined && (typeof v.maxMissions !== 'number' || !Number.isInteger(v.maxMissions) || v.maxMissions < 1 || v.maxMissions > AUTOPILOT_MAX_MISSIONS)) {
    return `Missions at once is a whole number from 1 to ${AUTOPILOT_MAX_MISSIONS}.`;
  }
  if (v.dailyCapUsd !== undefined && v.dailyCapUsd !== null && (typeof v.dailyCapUsd !== 'number' || !Number.isFinite(v.dailyCapUsd) || v.dailyCapUsd <= 0 || v.dailyCapUsd > 100_000)) {
    return 'The daily spend cap is a number of dollars above 0, or null for no cap.';
  }
  if (v.kinds !== undefined) {
    if (!Array.isArray(v.kinds) || !v.kinds.length) return 'Choose at least one item kind.';
    const bad = v.kinds.find((k) => !(AUTOPILOT_KINDS as readonly unknown[]).includes(k));
    if (bad !== undefined) return bad === 'epic' || bad === 'feature' ? 'Autopilot does not take epics or features: it takes their stories.' : `Autopilot takes ${AUTOPILOT_KINDS.join(', ')}.`;
  }
  if (v.permissionMode !== undefined && v.permissionMode !== null && !(SESSION_PERMISSION_MODES as readonly unknown[]).includes(v.permissionMode)) {
    return `The permission mode is one of ${SESSION_PERMISSION_MODES.join(', ')}, or null for the machine default.`;
  }
  if (v.review !== undefined && v.review !== null && !(ITEM_REVIEW_MODES as readonly unknown[]).includes(v.review)) {
    return `Review is ${ITEM_REVIEW_MODES.join(' or ')}, or null for the default (Orgus when Autonomy is full).`;
  }
  return null;
}

/** Settings with a valid patch applied. */
export function applyAutopilotPatch(prev: AutopilotSettings, patch: AutopilotPatch, by: string, at: Ts): AutopilotSettings {
  return {
    ...prev,
    ...(patch.on !== undefined ? { on: patch.on } : {}),
    ...(patch.maxMissions !== undefined ? { maxMissions: patch.maxMissions } : {}),
    ...('dailyCapUsd' in patch ? { dailyCapUsd: patch.dailyCapUsd ?? null } : {}),
    ...(patch.kinds !== undefined ? { kinds: [...new Set(patch.kinds)] } : {}),
    ...('permissionMode' in patch ? { permissionMode: patch.permissionMode && patch.permissionMode !== 'default' ? patch.permissionMode : null } : {}),
    ...('review' in patch ? { review: patch.review ?? null } : {}),
    by,
    at,
  };
}

// ---------------------------------------------------------------------------
// The pick
// ---------------------------------------------------------------------------

/** Why Autopilot leaves an item that is otherwise open work. */
export type AutopilotSkipCode = 'kind' | 'blocked' | 'parked' | 'no_estimate' | 'too_large' | 'held' | 'cited_on_main' | 'overlap';

export const AUTOPILOT_SKIP_LABEL: Record<AutopilotSkipCode, string> = {
  kind: 'Not a kind it takes',
  blocked: 'Blocked',
  parked: 'Parked',
  no_estimate: 'Not estimated',
  too_large: 'Too large',
  held: 'Needs a person',
  cited_on_main: 'Cited on main',
  overlap: 'Waits on overlap',
};

/** An item Autopilot left a person to look at (it failed twice, or a person stopped its mission). */
export interface AutopilotHold {
  reason: string;
  at: Ts;
}

export interface AutopilotCandidate {
  itemId: Id;
  key: string;
  title: string;
  kind: ItemKind;
  priority: BacklogItem['priority'];
}

export interface AutopilotSkip extends AutopilotCandidate {
  code: AutopilotSkipCode;
  /** One sentence: what holds it back and what makes it ready. */
  message: string;
}

export interface AutopilotPickInput {
  projectId: Id;
  items: readonly BacklogItem[];
  links: readonly ItemLink[];
  kinds: readonly ItemKind[];
  /** Items a live mission already works. */
  active: ReadonlySet<Id>;
  holds: Readonly<Record<Id, AutopilotHold>>;
  /** R-MS-6: running missions and the files their items expect to change; an overlapping item waits. */
  running?: readonly AutopilotRunning[];
  /** Now, for the 2-hour cap on an overlap wait. */
  now?: Ts;
}

/** A running mission, as the overlap check sees it (R-MS-6). */
export interface AutopilotRunning {
  key: string;
  files: readonly string[];
  startedAt: Ts;
}

/** How long a queued item waits for an overlapping running mission before it starts anyway. */
export const OVERLAP_WAIT_MS = 2 * 60 * 60_000;

const FILE_RE = /(?<![\w./:@-])(?:[\w.@-]+\/)+[\w.@-]+\.[A-Za-z0-9]{1,8}(?![\w/])/g;

/** The repo files an item's text names (`apps/hub/src/ext/missions/index.ts`): what its mission expects to change. */
export function expectedFiles(it: Pick<BacklogItem, 'title' | 'body'>): string[] {
  const out = new Set<string>();
  for (const m of `${it.title}\n${it.body ?? ''}`.matchAll(FILE_RE)) {
    out.add(m[0]);
  }
  return [...out];
}

/** The first running mission whose files overlap these, and the shared file; null when none (or the wait is over). */
export function overlapOf(files: readonly string[], running: readonly AutopilotRunning[], now: Ts): { key: string; file: string } | null {
  if (!files.length) return null;
  for (const r of running) {
    if (now - r.startedAt >= OVERLAP_WAIT_MS) continue;
    const file = files.find((f) => r.files.includes(f));
    if (file) return { key: r.key, file };
  }
  return null;
}

/** Open work Autopilot looks at: waiting to start, not yet worked by a mission. */
function waiting(it: BacklogItem): boolean {
  return it.status === 'backlog' || it.status === 'ready';
}

/** Why Autopilot skips this item, or null when it may start it. */
export function autopilotSkipOf(it: BacklogItem, input: Pick<AutopilotPickInput, 'items' | 'links' | 'kinds' | 'holds' | 'running' | 'now'>, byId?: ReadonlyMap<Id, BacklogItem>): { code: AutopilotSkipCode; message: string } | null {
  const hold = input.holds[it.id];
  if (hold) return { code: 'held', message: `${hold.reason} Change the item to hand it back.` };
  if (!input.kinds.includes(it.kind)) {
    return it.kind === 'epic' || it.kind === 'feature'
      ? { code: 'kind', message: `${ITEM_KIND_LABEL[it.kind]}s run as their stories, not as one mission.` }
      : { code: 'kind', message: `Autopilot here takes ${input.kinds.map((k) => ITEM_KIND_LABEL[k].toLowerCase()).join(', ')}.` };
  }
  if (it.cited) return { code: 'cited_on_main', message: `Cited by ${it.cited.sha.slice(0, 7)} on main. Mark it done, or remove the citation note to hand it back.` };
  if (it.labels.includes(PARKED_LABEL)) return { code: 'parked', message: `Remove the ${PARKED_LABEL} label to hand it over.` };
  const map = byId ?? new Map(input.items.map((i) => [i.id, i]));
  const blockers = openBlockers(it.id, input.links, map);
  if (blockers.length) {
    const first = map.get(blockers[0]!);
    return { code: 'blocked', message: `Blocked by ${first ? first.key : 'another item'}${blockers.length > 1 ? ` and ${blockers.length - 1} more` : ''}, not done yet.` };
  }
  if (it.estimate == null) return { code: 'no_estimate', message: 'Estimate it to hand it over.' };
  const large = tooLarge(it);
  if (large) return { code: 'too_large', message: `At ${large} it is too large for one mission. Split it.` };
  const o = input.running?.length ? overlapOf(expectedFiles(it), input.running, input.now ?? Date.now()) : null;
  if (o) return { code: 'overlap', message: `Waits for ${o.key}: both change ${o.file}. It starts when ${o.key} lands, or within 2 hours.` };
  return null;
}

// ---------------------------------------------------------------------------
// Grooming (ORGU-155, R-MS-9, ADR 0087 decision 2)
// ---------------------------------------------------------------------------

/** Grooming jobs Autopilot runs at once per project, at most. */
export const AUTOPILOT_GROOM_JOBS = 2;

/** Words that make a criterion a matter of taste unless a number or a named check pins it down. */
const VAGUE_RE = /\b(works? (well|fine|correctly|properly)|user[- ]friendly|intuitive|fast|quick(ly)?|nice|good|better|easy|easily|properly|as expected|seamless(ly)?|robust|clean|etc|tbd|somehow|appropriate(ly)?)\b/i;
const PINNED_RE = /\d|\b(test|returns?|shows?|status|error|exit|code|file|log|api|click|button|field|equals?|contains?)\b/i;

/**
 * Why a criterion cannot be checked by a mission, or null when it can: too short to say what to
 * check, or vague (fast, user-friendly, works well) with nothing measurable beside it.
 */
export function uncheckableWhy(ac: { text: string; given?: string; when?: string; then?: string }): string | null {
  const text = [ac.text, ac.given, ac.when, ac.then].filter(Boolean).join(' ').trim();
  if (text.split(/\s+/).filter(Boolean).length < 3) return 'it is too short to say what to check';
  const vague = VAGUE_RE.exec(text);
  if (vague && !PINNED_RE.test(text)) return `"${vague[0]}" has no measure a mission could check`;
  return null;
}

/** The first criterion of an item that cannot be checked, with why. */
export function firstUncheckable(it: Pick<BacklogItem, 'acceptance'>): { id: string; text: string; why: string } | null {
  for (const ac of it.acceptance) {
    const why = uncheckableWhy(ac);
    if (why) return { id: ac.id, text: ac.text, why };
  }
  return null;
}

/**
 * What Autopilot takes in a project, in order (highest priority first, then oldest), and what it
 * skips with the reason. Items that are not waiting (in progress, in review, done, dropped, a
 * defect still New) are neither, except those it holds for a person, which show as skipped.
 */
export function autopilotPick(input: AutopilotPickInput): { ready: AutopilotCandidate[]; skipped: AutopilotSkip[] } {
  const byId = new Map(input.items.map((i) => [i.id, i]));
  const ready: (AutopilotCandidate & { createdAt: Ts })[] = [];
  const skipped: (AutopilotSkip & { createdAt: Ts })[] = [];
  for (const it of input.items) {
    if (it.projectId !== input.projectId || input.active.has(it.id)) continue;
    const held = !!input.holds[it.id] && it.status !== 'done' && it.status !== 'dropped';
    if (!waiting(it) && !held) continue;
    const base = { itemId: it.id, key: it.key, title: it.title, kind: it.kind, priority: it.priority, createdAt: it.createdAt };
    const skip = autopilotSkipOf(it, input, byId);
    if (skip) {
      // Epics and features are never work for a mission: listing every one would bury the rest.
      if (skip.code === 'kind' && (it.kind === 'epic' || it.kind === 'feature')) continue;
      skipped.push({ ...base, ...skip });
    } else ready.push(base);
  }
  const order = (a: { priority: number; createdAt: Ts; key: string }, b: { priority: number; createdAt: Ts; key: string }) => a.priority - b.priority || a.createdAt - b.createdAt || a.key.localeCompare(b.key);
  const strip = <T extends { createdAt: Ts }>({ createdAt: _c, ...rest }: T) => rest;
  return { ready: ready.sort(order).map(strip), skipped: skipped.sort(order).map(strip) };
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

/**
 * Why Autopilot starts nothing right now:
 *   off        it is switched off
 *   limit      it already runs as many missions as it may
 *   cap        its missions spent the day's cap
 *   main-red   Orgus Watch has an open red-main incident
 *   full       no machine with this project checked out has room (ADR 0063 admission)
 *   no-machine no online machine has this project checked out
 *   empty      nothing is ready
 *   usage-limit  no agent can run: each is cooling down after a failure of its own (a usage limit,
 *                signed out), or, without module 'scheduler', a mission's agent hit its usage limit
 */
export type AutopilotWait = 'off' | 'paused' | 'limit' | 'cap' | 'main-red' | 'full' | 'no-machine' | 'empty' | 'usage-limit' | 'start-failing';

/** How long Autopilot pauses a project's starts after an agent hits its usage limit (without a reset time). */
export const AUTOPILOT_USAGE_LIMIT_PAUSE_MS = 30 * 60_000;

/** One agent kind Autopilot cannot use now: cooling down after a failure of its own, until `until`. */
export interface AutopilotLimitedAgent {
  kind: string;
  cause: FailureCause;
  until: Ts;
}

const hhmm = (ts: Ts): string => `${new Date(ts).toISOString().slice(11, 16)} UTC`;

/**
 * The wait text when no agent can run any of a project's missions (ADR 0049 decision 5): which
 * agents are limited, when each resets, and when Autopilot starts again (the soonest reset).
 * "No agent can run now: Codex is cooling down (api error) until 13:15 UTC; Claude Code hit its usage
 * limit until 14:00 UTC. It starts again at 13:15 UTC. Its missions do not count as failed."
 */
export function autopilotLimitedText(list: readonly AutopilotLimitedAgent[]): string {
  const byKind = new Map<string, AutopilotLimitedAgent>();
  for (const a of list) {
    const cur = byKind.get(a.kind);
    if (!cur || a.until < cur.until) byKind.set(a.kind, a);
  }
  const each = [...byKind.values()].sort((a, b) => a.until - b.until);
  if (!each.length) return 'No agent can run now. Its missions do not count as failed.';
  const parts = each.map((a) =>
    a.cause === 'usage-limit' ? `${agentKindLabel(a.kind)} hit its usage limit until ${hhmm(a.until)}` : `${agentKindLabel(a.kind)} is cooling down (${FAILURE_CAUSE_LABEL[a.cause].toLowerCase()}) until ${hhmm(a.until)}`,
  );
  return `No agent can run now: ${parts.join('; ')}. It starts again at ${hhmm(each[0]!.until)}. Its missions do not count as failed.`;
}

/** A session's text says its account hit a usage limit ("You've hit your session limit", "usage limit reached"). */
export function isUsageLimitText(text: string | null | undefined): boolean {
  return !!text && /\b(hit your (session|usage|weekly) limit|usage limit (reached|exceeded)|rate limit (reached|exceeded))\b/i.test(text);
}

export interface AutopilotStatus {
  projectId: Id;
  settings: AutopilotSettings;
  /** null: it starts the next item on this tick. */
  wait: AutopilotWait | null;
  /** One plain sentence for the wait, or what it does. */
  text: string;
  /** Autopilot missions open in this project now. */
  running: Id[];
  /** USD its missions spent today; null when the costs module is not loaded. */
  spentToday: number | null;
  next: AutopilotCandidate | null;
  /** Ready items after `next`, in order (at most 10). */
  queue: AutopilotCandidate[];
  /** At most 50, in the same order. */
  skipped: AutopilotSkip[];
  /** The last start, for the page's "last started" line. */
  lastStart?: { missionId: Id; itemId: Id; key: string; at: Ts; ok: boolean; message?: string } | null;
  /** The last pass of the tick over this project (ADR 0094): kept in memory, so absent after a restart until the next pass. */
  lastPass?: AutopilotPass | null;
}

/** One pass of Autopilot's tick over a project: when, where its ladder stopped (or that it started a mission), why, and what it saw. */
export interface AutopilotPass {
  at: Ts;
  /** The check that stopped it, as the wait it reports, or 'started'. */
  result: AutopilotWait | 'started';
  /** The one-line reason, the status text of that pass. */
  reason: string;
  counts: { ready: number; skipped: number; running: number };
}

export function autopilotWaitText(wait: AutopilotWait | null, s: Pick<AutopilotStatus, 'settings' | 'running' | 'next'>, detail = ''): string {
  switch (wait) {
    case 'off':
      return 'Autopilot is off.';
    case 'paused':
      return 'Missions are paused. Nothing new starts until a person resumes them.';
    case 'limit':
      return `Running ${s.running.length} of ${s.settings.maxMissions} missions. The next starts when one finishes.`;
    case 'cap':
      return "Today's spend cap is reached. It starts again tomorrow.";
    case 'main-red':
      // The detail is the Watch incident's own title, which already says main is red.
      return `${detail || 'Main is red'}. It starts again once main is green.`;
    case 'full':
      return `No machine has room${detail ? `: ${detail}` : ''}. It starts when room frees up.`;
    case 'no-machine':
      return 'No online machine has this project checked out.';
    case 'empty':
      return 'Nothing is ready to start.';
    case 'usage-limit':
      return `The agent hit its usage limit${detail ? `; it starts again ${detail}` : ''}. Its missions do not count as failed.`;
    case 'start-failing':
      // ORGU-405: the detail is the error the starts kept failing with.
      return `Paused: its starts keep failing${detail ? ` (${detail})` : ''}. It resumes after a successful start, or when a person switches it on.`;
    default:
      return s.next ? `Starting ${s.next.key} next.` : 'Watching for ready items.';
  }
}

/** Characters of the branch after mission/: worktree paths stay well inside the Windows limit. */
const BRANCH_SLUG_MAX = 48;

/** The branch an Autopilot mission works on: mission/<key>-<title words>, lower case, whole words. */
export function autopilotBranch(it: Pick<BacklogItem, 'key' | 'title'>): string {
  let slug = `${it.key} ${it.title}`
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (slug.length > BRANCH_SLUG_MAX) {
    const cut = slug.slice(0, BRANCH_SLUG_MAX + 1);
    const word = cut.lastIndexOf('-');
    slug = (word > 0 ? cut.slice(0, word) : cut.slice(0, BRANCH_SLUG_MAX)).replace(/-+$/g, '');
  }
  return `mission/${slug || 'item'}`;
}

/** The goal of an Autopilot mission. */
export function autopilotGoal(it: Pick<BacklogItem, 'key' | 'kind'>): string {
  return `Every acceptance criterion of ${ITEM_KIND_LABEL[it.kind].toLowerCase()} ${it.key} is met and the work is committed on this branch. Orgus lands it once you stop: do not run orgus land. Run only the tests of the files you changed; do not run pnpm test:affected or wait on any check before committing: the landing runs the related checks and Orgus wakes you with what failed. Decide and continue; never end a turn on a question.`;
}
